import { useMutation } from "@apollo/client";
import { Button, Container, Icon, useModal } from "@fluidattacks/design";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { AddMembersModal } from "../../modals/add-modal";
import { UPDATE_STAKEHOLDER_MUTATION } from "../queries";
import { handleEditError } from "../utils";
import type { IStakeholderFormValues } from "features/add-user-modal/types";
import type { OrganizationRole } from "gql/graphql";
import type { IStakeholderDataSet } from "pages/organization/members/types";
import { msgSuccess } from "utils/notifications";

interface IEditActionProps {
  organizationId: string;
  selectedMembers: IStakeholderDataSet[];
  allMembers: IStakeholderDataSet[];
  resetOnAction: VoidFunction;
  disabled?: boolean;
}

const EditAction = (props: IEditActionProps): JSX.Element => {
  const {
    organizationId,
    resetOnAction,
    selectedMembers,
    allMembers,
    disabled = false,
  } = props;
  const { t } = useTranslation();

  const modalRef = useModal("editStakeholderModal");
  const onClick = useCallback((): void => {
    modalRef.open();
  }, [modalRef]);

  const [updateStakeholder] = useMutation(UPDATE_STAKEHOLDER_MUTATION, {
    onCompleted: (mtResult): void => {
      if (mtResult.updateOrganizationStakeholder.success) {
        const { email } =
          mtResult.updateOrganizationStakeholder.modifiedStakeholder ?? {};

        msgSuccess(
          `${email} ${t("organization.tabs.users.editButton.success")}`,
          t("organization.tabs.users.successTitle"),
        );
      }
    },
    onError: handleEditError,
  });

  const handleSubmit = useCallback(
    async (values: IStakeholderFormValues): Promise<void> => {
      await updateStakeholder({
        variables: {
          email: values.email,
          organizationId,
          role: values.role as OrganizationRole,
        },
      });
      resetOnAction();
      modalRef.close();
    },
    [updateStakeholder, organizationId, resetOnAction, modalRef],
  );

  return (
    <React.Fragment>
      <Button
        disabled={disabled}
        id={"members-edit-button"}
        justify={"start"}
        onClick={onClick}
        variant={"ghost"}
        width={"100%"}
      >
        <Container justify={"start"} pl={0.5}>
          <Icon
            icon={"edit"}
            iconSize={"lg"}
            iconType={"fa-light"}
            mr={0.25}
          ></Icon>
          {t("organization.tabs.users.editButton.text")}
        </Container>
      </Button>
      {modalRef.isOpen && (
        <AddMembersModal
          action={"edit"}
          allMembers={allMembers}
          close={modalRef.close}
          isOpen={modalRef.isOpen}
          onSubmit={handleSubmit}
          organizationId={organizationId}
          selectedMembers={selectedMembers}
        />
      )}
    </React.Fragment>
  );
};

export { EditAction };
